import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, FormControl, Select, MenuItem, Typography, SelectChangeEvent } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { RetailerByValueDto, UserFranchiseList } from '../../../../app/services/api/ofdClient';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useTheme } from '@mui/material/styles';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { UserContext } from '../../../../components/shared/useUser';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';

interface RetailersByValueProps {
  retailers: RetailerByValueDto[];
  currencyCode?: string;
}

const RetailersByValue = ({ retailers, currencyCode }: RetailersByValueProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();

  const chartData = useMemo(() => retailers.slice(0, 10).map((r, idx) => ({
    id: `${r.customerId}-${idx}`,
    name: (r.customerName?.length ?? 0) > 20 ? `${r.customerName?.substring(0, 20)}...` : r.customerName || r.customerNumber || t('Unknown'),
    fullName: r.customerName || r.customerNumber || t('Unknown'),
    value: r.totalValue ?? 0,
    orders: r.orderCount ?? 0,
  })), [retailers, t]);

  const formatCurrency = useCallback(
    (value: number) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'GBP',
        maximumFractionDigits: 0,
      }).format(value),
    [currencyCode],
  );

  const tooltipFormatter = useCallback(
    (value: number) =>
      [formatCurrency(value), t('Total Value')],
    [formatCurrency, t],
  );

  const tooltipLabelFormatter = useCallback((
    _: unknown,
    payload: Array<{ payload?: { fullName?: string; orders?: number } }>,
  ) => {
    const data = payload?.[0]?.payload;
    return data ? `${data.fullName} (${data.orders} ${t('orders')})` : '';
  }, [t]);

  const colors = useMemo(() => [
    theme.palette.primary.main, theme.palette.primary.light, theme.palette.secondary.main, theme.palette.secondary.light,
    theme.palette.success.main, theme.palette.success.light, theme.palette.info.main, theme.palette.info.light,
    theme.palette.warning.main, theme.palette.warning.light,
  ], [theme]);

  if (!retailers?.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography
          variant="textSM"
          color="text.secondary"
        >
          {t('No retailer data available for this period')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 350 }}>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            tickFormatter={formatCurrency}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={150}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={tooltipFormatter}
            labelFormatter={tooltipLabelFormatter}
          />
          <Bar
            dataKey="value"
            name={t('Total Value')}
          >
            {chartData.map((item, index) => (
              <Cell
                key={item.id}
                fill={colors[index % colors.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const RetailersByValueWidget = () => {
  const { franchises } = useContext(UserContext);
  const { getDashboardRetailersByValue } = useNswagClient();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [retailers, setRetailers] = useState<RetailerByValueDto[]>([]);
  const [period, setPeriod] = useState<string>('last-month');
  const [isLoading, setIsLoading] = useState(true);
  const [customFromDate, setCustomFromDate] = useState<Dayjs | null>(null);
  const [customToDate, setCustomToDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  const calculateDatesFromPeriod = (selectedPeriod: string): { fromDate: string, toDate: string } => {
    const now = dayjs();
    const toDate = now.toISOString();
    let fromDate: Dayjs;

    switch (selectedPeriod) {
      case 'over-time':
        // All time - start from a very early date
        fromDate = dayjs('2000-01-01');
        break;
      case 'last-12-months':
        fromDate = now.subtract(12, 'month');
        break;
      case 'last-month':
        fromDate = now.subtract(1, 'month');
        break;
      case 'last-week':
        fromDate = now.subtract(7, 'day');
        break;
      case 'defined-period':
        if (customFromDate && customToDate) {
          return {
            fromDate: customFromDate.toISOString(),
            toDate: customToDate.toISOString(),
          };
        }
        // Fallback to last month if custom dates not set
        fromDate = now.subtract(1, 'month');
        break;
      default:
        fromDate = now.subtract(1, 'month');
    }

    return {
      fromDate: fromDate.toISOString(),
      toDate,
    };
  };

  useEffect(() => {
    /**
     * Fetches retailer data by value for the selected period and franchises
     * Builds date range from period selection and fetches corresponding data
     */
    const fetchRetailersByValue = async () => {
      setIsLoading(true);
      try {
        const { fromDate, toDate } = calculateDatesFromPeriod(period);

        // Build user franchises payload with both names and IDs
        const userFranchises: UserFranchiseList = {
          franchiseNames: franchises
            .filter(f => f.franchiseName)
            .map(f => f.franchiseName as string),
          franchiseIds: franchises
            .filter(f => f.franchiseId)
            .map(f => f.franchiseId as number),
          otherRegions: false,
          currentFranchiseName: '',
          taskView: false,
        };

        const response = await getDashboardRetailersByValue(fromDate, toDate, userFranchises);
        setRetailers(response.retailers || []);
      } catch (error) {
        setRetailers([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRetailersByValue().catch(() => {
      // Error already handled in try-catch above
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, franchises, customFromDate, customToDate]);

  const handlePeriodChange = useCallback((event: SelectChangeEvent<string>) => {
    setPeriod(event.target.value);
  }, []);

  const handleCustomFromDateChange = useCallback((date: Dayjs | null) => {
    setCustomFromDate(date);
  }, []);

  const handleCustomToDateChange = useCallback((date: Dayjs | null) => {
    setCustomToDate(date);
  }, []);

  const periodSelectorProps = {
    value: period,
    onChange: handlePeriodChange,
    displayEmpty: true,
  };

  const datePickerSlotProps = {
    textField: {
      size: 'small' as const,
      sx: { minWidth: 150 },
    },
  };

  /**
   * Renders the period selector dropdown with various time range options
   * @returns Period selector component with dropdown menu
   */
  const renderPeriodSelector = () => (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}
    >
      <Typography color="text.secondary">Period:</Typography>
      <FormControl
        size="small"
        sx={{ minWidth: 150 }}
      >
        <Select {...periodSelectorProps}>
          <MenuItem value="over-time">Over Time</MenuItem>
          <MenuItem value="last-12-months">Last 12 Months</MenuItem>
          <MenuItem value="last-month">Last Month</MenuItem>
          <MenuItem value="last-week">Last Week</MenuItem>
          <MenuItem value="defined-period">Defined Period</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  /**
   * Renders custom date range pickers for selecting from and to dates
   * @returns Date range picker component with from and to date inputs
   */
  const renderDateRangePicker = () => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <DatePicker
          label="From Date"
          value={customFromDate}
          onChange={handleCustomFromDateChange}
          slotProps={datePickerSlotProps}
        />
        <DatePicker
          label="To Date"
          value={customToDate}
          onChange={handleCustomToDateChange}
          slotProps={datePickerSlotProps}
        />
      </Box>
    </LocalizationProvider>
  );

  /**
   * Renders the main content area showing either loading state or retailers data
   * @returns Loading message or RetailersByValue component with data
   */
  const renderContent = () => {
    if (isLoading) {
      return (
        <Typography
          color="text.secondary"
          sx={{ textAlign: 'center', py: 4 }}
        >
          Loading...
        </Typography>
      );
    }
    return (
      <RetailersByValue
        retailers={retailers}
        currencyCode={currencyCode}
      />
    );
  };

  return (
    <Box>
      {renderPeriodSelector()}
      {period === 'defined-period' && renderDateRangePicker()}
      {renderContent()}
    </Box>
  );
};

export default RetailersByValueWidget;
